import { Location } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { BookingService, BookingConfirmationTarget } from '../services/booking.service'
import { Context } from '../domain/Context'
import { BookingDetails } from '../domain/BookingDetails'
import { bookingPaymentTerminologyForVenueId } from '../domain/BookingPaymentTerminology'
import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Subject, takeUntil } from 'rxjs'
import { Venue } from '../domain/Venue'
import { VenueService } from '../services/venue.service'

@Component({
    selector: 'app-confirm-card',
    templateUrl: './confirm-card.component.html',
})
export class ConfirmCardComponent implements OnInit, OnDestroy {

    @Input() bookingDetails!: BookingDetails
    form!: FormGroup
    venue: Venue | null = null
    isLoadingVenue = false
    isConfirmingPayment = false
    errorLoadingVenue = false
    errorConfirmingPayment = false
    cardSetupFailed = false
    private confirmationTarget: BookingConfirmationTarget | null = null
    private pollingStarted = false
    private onDestroy$ = new Subject<void>()

    get paymentTerminology() {
        return bookingPaymentTerminologyForVenueId(this.bookingDetails?.venueId, this.bookingDetails?.hasUnpaidOrder)
    }

    constructor(
        private changeDetector: ChangeDetectorRef,
        private fb: FormBuilder,
        private modal: NgbActiveModal,
        private venueService: VenueService,
        private bookingService: BookingService,
        private location: Location
    ) { }

    ngOnInit() {
        const query = new URLSearchParams(this.location.path().split('?')[1] ?? '')
        const setupIntentId = query.get('setupIntentId')
        const paymentIntentId = query.get('paymentIntentId')
        this.confirmationTarget = setupIntentId ? { setupIntentId } : paymentIntentId ? { paymentIntentId } : null
        this.isConfirmingPayment = this.confirmationTarget !== null
        this.form = this.makeForm()
        this.loadVenue()
        this.bindCancellationChargeSetupIntentIdToConfirmingPayment()
        this.bindDepositPaymentIntentIdToConfirmingPayment()
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    dismiss() {
        this.modal.dismiss()
    }

    confirmWithCardStarted(kind: 'setupIntentId' | 'paymentIntentId', intentId: string) {
        this.errorConfirmingPayment = false
        this.cardSetupFailed = false
        if (intentId) {
            this.confirmationTarget = kind === 'setupIntentId' ? { setupIntentId: intentId } : { paymentIntentId: intentId }
            this.setRecoveryMarker(this.confirmationTarget)
        }
        this.changeDetector.detectChanges()
    }

    confirmWithCardFailed() {
        if (!this.isConfirmingPayment) {
            this.setRecoveryMarker(null)
        }
    }

    private setRecoveryMarker(target: BookingConfirmationTarget | null) {
        const [path, query = ''] = this.location.path().split('?')
        const params = new URLSearchParams(query)
        params.delete('setupIntentId')
        params.delete('paymentIntentId')
        if (target) {
            for (const [key, value] of Object.entries(target)) {
                params.set(key, value)
            }
            // Preserve Dojo's explicit hosted setup confirmation when refreshing before redirect.
            if ('setupIntentId' in target && this.venue?.paymentsAccountForCapability('CancellationChargePayments')?.gateway === 'dojo') {
                params.set('setupBookingId', this.bookingDetails.bookingId)
            }
        } else if (params.get('setupBookingId') === this.bookingDetails.bookingId) {
            params.delete('setupBookingId')
        }
        this.location.replaceState(path, params.toString())
    }

    private waitForConfirmation() {
        if (!this.venue || !this.confirmationTarget || this.pollingStarted || !this.isConfirmingPayment) {
            return
        }
        this.pollingStarted = true
        this.setRecoveryMarker(this.confirmationTarget)
        this.bookingService.waitForPaymentConfirmation(
            new Context(this.venue.organisationId, this.venue.businessId, this.venue.id),
            this.confirmationTarget
        ).pipe(takeUntil(this.onDestroy$)).subscribe({
            next: details => this.cardDetailsConfirmationReceived(details),
            error: error => {
                if (error instanceof HttpErrorResponse && error.status === 400 && this.venue?.paymentsAccountForCapability('CancellationChargePayments')?.gateway === 'teya'
                    && this.confirmationTarget && 'setupIntentId' in this.confirmationTarget) {
                    this.isConfirmingPayment = false
                    this.pollingStarted = false
                    this.confirmationTarget = null
                    this.cardSetupFailed = true
                    this.form.get('PaymentDetails.setupIntentId')?.setValue(null)
                    this.setRecoveryMarker(null)
                }
                this.errorConfirmingPayment = true
                this.changeDetector.detectChanges()
            },
        })
    }

    private makeForm() {
        return this.fb.group({
            'PaymentDetails': this.fb.group({
                setupIntentId: [null, []],
            }),
            'Deposit': this.fb.group({
                paymentIntentId: [null, []],
            }),
        })
    }

    private loadVenue() {
        this.isLoadingVenue = true
        this.errorLoadingVenue = false
        this.changeDetector.detectChanges()
        this.venueService.getWithId(this.bookingDetails.venueId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: venue => {
                    this.venue = venue
                    this.isLoadingVenue = false
                    this.waitForConfirmation()
                    this.changeDetector.detectChanges()
                },
                error: () => {
                    this.errorLoadingVenue = true
                    this.isLoadingVenue = false
                    this.changeDetector.detectChanges()
                },
            })
    }

    private bindCancellationChargeSetupIntentIdToConfirmingPayment() {
        this.form.get('PaymentDetails.setupIntentId')?.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: (setupIntentId) => {
                    if (setupIntentId) {
                        this.confirmationTarget = { setupIntentId }
                        this.isConfirmingPayment = true
                        this.waitForConfirmation()
                        this.changeDetector.detectChanges()
                    }
                },
            })
    }

    cardDetailsConfirmationReceived(bookingDetails: BookingDetails) {
        if (bookingDetails.bookingId !== this.bookingDetails.bookingId) {
            this.errorConfirmingPayment = true
            this.changeDetector.detectChanges()
            return
        }
        this.bookingDetails = bookingDetails
        this.setRecoveryMarker(null)
        this.modal.close(bookingDetails)
        this.changeDetector.detectChanges()
    }

    private bindDepositPaymentIntentIdToConfirmingPayment() {
        this.form.get('Deposit.paymentIntentId')?.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: (paymentIntentId) => {
                    if (paymentIntentId) {
                        this.confirmationTarget = { paymentIntentId }
                        this.isConfirmingPayment = true
                        this.waitForConfirmation()
                        this.changeDetector.detectChanges()
                    }
                },
            })
    }
}
